import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Component, Inject, OnInit, PLATFORM_ID, Renderer2, DOCUMENT } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { LayoutService } from './core/layout.service';
import { GtmService } from './core/gtm.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false
})
export class AppComponent implements OnInit {
  private readonly locales = ['ar', 'de', 'es', 'fr', 'id', 'it', 'ja', 'pl', 'pt', 'ru', 'zh'];
  private readonly localizedSeo: Record<string, { title: string; description: string; ogLocale: string }> = {
    ar: { title: 'CaseActive | بوابة العملاء لمكاتب المحاماة', description: 'تساعد CaseActive مكاتب المحاماة على إبقاء العملاء على اطلاع من خلال بوابة آمنة تحمل علامتها التجارية وتحديثات تلقائية.', ogLocale: 'ar_AR' },
    de: { title: 'CaseActive | Mandantenportal für Kanzleien', description: 'CaseActive hilft Kanzleien, Mandanten mit einem sicheren, markeneigenen Portal und automatischen Aktualisierungen auf dem Laufenden zu halten.', ogLocale: 'de_DE' },
    es: { title: 'CaseActive | Portal de clientes para bufetes', description: 'CaseActive ayuda a los bufetes a mantener informados a sus clientes mediante un portal seguro con su marca y actualizaciones automáticas.', ogLocale: 'es_ES' },
    fr: { title: 'CaseActive | Portail client pour cabinets juridiques', description: 'CaseActive aide les cabinets juridiques à informer leurs clients grâce à un portail sécurisé à leur image et à des mises à jour automatiques.', ogLocale: 'fr_FR' },
    id: { title: 'CaseActive | Portal klien untuk firma hukum', description: 'CaseActive membantu firma hukum memberi informasi terbaru kepada klien melalui portal aman bermerek dan pembaruan otomatis.', ogLocale: 'id_ID' },
    it: { title: 'CaseActive | Portale clienti per studi legali', description: 'CaseActive aiuta gli studi legali a tenere informati i clienti tramite un portale sicuro personalizzato e aggiornamenti automatici.', ogLocale: 'it_IT' },
    ja: { title: 'CaseActive | 法律事務所向けクライアントポータル', description: 'CaseActiveは、安全なブランド付きポータルと自動更新により、法律事務所と依頼者の円滑な情報共有を支援します。', ogLocale: 'ja_JP' },
    pl: { title: 'CaseActive | Portal klienta dla kancelarii', description: 'CaseActive pomaga kancelariom informować klientów za pomocą bezpiecznego, markowego portalu i automatycznych aktualizacji.', ogLocale: 'pl_PL' },
    pt: { title: 'CaseActive | Portal de clientes para escritórios', description: 'A CaseActive ajuda escritórios de advocacia a manter os clientes informados com um portal seguro e personalizado e atualizações automáticas.', ogLocale: 'pt_BR' },
    ru: { title: 'CaseActive | Клиентский портал для юридических фирм', description: 'CaseActive помогает юридическим фирмам информировать клиентов через безопасный брендированный портал и автоматические обновления.', ogLocale: 'ru_RU' },
    zh: { title: 'CaseActive | 律师事务所客户门户', description: 'CaseActive 通过安全的品牌客户门户和自动更新，帮助律师事务所让客户及时了解案件进展。', ogLocale: 'zh_CN' },
  };
  public isHandset$: Observable<boolean>;

  constructor(
    private renderer: Renderer2,
    private layoutService: LayoutService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object,
    private gtmService: GtmService,
    private router: Router,
    private meta: Meta,
    private title: Title,
  ) {
    this.gtmService.addGtm();
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      const html = this.document.querySelector('html');

      this.renderer.addClass(html, 'js');

      this.isHandset$ = this.layoutService.isHandset$.pipe(
        tap(isHandset =>
          isHandset ? this.renderer.removeClass(html, 'no-mobile') : this.renderer.addClass(html, 'no-mobile')
        )
      );
    }

    this.setCanonicalTag(this.router.url);

    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe((e: NavigationEnd) => {
      this.setCanonicalTag(e.urlAfterRedirects);
    });
  }

  private setCanonicalTag(url: string): void {
    const cleanUrl = this.normalizeCanonicalPath(url);
    const locale = this.getLocale(cleanUrl);
    const canonicalUrl = 'https://www.caseactive.com' + this.removeLocalePrefix(cleanUrl);

    let link = this.document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.renderer.createElement('link');
      this.renderer.setAttribute(link, 'rel', 'canonical');
      this.renderer.appendChild(this.document.head, link);
    }
    this.renderer.setAttribute(link, 'href', canonicalUrl);

    this.setDocumentLocale(locale);
    this.setRobotsTag(locale);
    this.setLocalizedMetadata(locale);
    this.setHreflangTags(cleanUrl);
  }

  private getLocale(path: string): string | null {
    const firstSegment = path.split('/').filter(Boolean)[0];

    return this.locales.includes(firstSegment) ? firstSegment : null;
  }

  private removeLocalePrefix(path: string): string {
    const segments = path.split('/').filter(Boolean);

    if (this.locales.includes(segments[0])) {
      segments.shift();
    }

    return segments.length ? `/${segments.join('/')}` : '/';
  }

  private setDocumentLocale(locale: string | null): void {
    const html = this.document.documentElement;

    this.renderer.setAttribute(html, 'lang', locale ?? 'en');
    this.renderer.setAttribute(html, 'dir', locale === 'ar' ? 'rtl' : 'ltr');
  }

  private setRobotsTag(locale: string | null): void {
    if (locale) {
      this.meta.updateTag({ name: 'robots', content: 'noindex, follow' });
    } else {
      this.meta.removeTag('name="robots"');
    }
  }

  private setLocalizedMetadata(locale: string | null): void {
    if (!locale) return;

    const seo = this.localizedSeo[locale];
    this.title.setTitle(seo.title);
    this.meta.updateTag({ name: 'title', content: seo.title });
    this.meta.updateTag({ name: 'description', content: seo.description });
    this.meta.updateTag({ property: 'og:title', content: seo.title });
    this.meta.updateTag({ property: 'og:description', content: seo.description });
    this.meta.updateTag({ property: 'og:locale', content: seo.ogLocale });
  }

  private setHreflangTags(currentPath: string): void {
    this.document.querySelectorAll('link[rel="alternate"]').forEach(el => el.remove());

    const base = 'https://www.caseactive.com';
    const englishPath = this.removeLocalePrefix(currentPath);

    const hreflangEntries = [
      { hreflang: 'en', href: `${base}${englishPath || '/'}` },
      { hreflang: 'x-default', href: `${base}${englishPath || '/'}` },
    ];

    hreflangEntries.forEach(({ hreflang, href }) => {
      const link = this.renderer.createElement('link');
      this.renderer.setAttribute(link, 'rel', 'alternate');
      this.renderer.setAttribute(link, 'hreflang', hreflang);
      this.renderer.setAttribute(link, 'href', href);
      this.renderer.appendChild(this.document.head, link);
    });
  }

  private normalizeCanonicalPath(url: string): string {
    const path = (url.split('?')[0].replace(/\/+$/, '') || '/');
    const segments = path.split('/').filter(Boolean);
    const hasLocale = this.locales.includes(segments[0]);
    const offset = hasLocale ? 1 : 0;

    if (segments[offset] === 'docs') {
      segments[offset] = 'documentation';
    }

    if (segments[offset] === 'term-of-service') {
      segments[offset] = 'terms-of-service';
    }

    if (segments[offset] === 'home') {
      return hasLocale ? `/${segments[0]}` : '/';
    }

    if (segments[offset] === 'solutions') {
      segments.splice(offset, segments.length - offset, 'overview');
    }

    return segments.length ? `/${segments.join('/')}` : '/';
  }
}
